<script setup lang="ts">
import { TITLE_PAGE, TRANSITION_SOURCE } from '#shared/constant/constant'
import type {
  CarModel,
  CarModelResponse,
  CarModelContents,
} from '#shared/interfaces/car-model-list/model'
import { postWindowBridge } from '@/utils/windowBridge'
import { buildAsyncDataKey } from '@/utils/asyncDataKey'

const modelList = ref<Array<CarModel>>([])
const filteredModelList = ref<Array<CarModel>>([])
const displayList = ref<Array<CarModel>>([])
const sectionHeight = ref(0)
const route = useRoute()
const query = route.query
const isLoading = ref(true)
const pagePosition = useState('page_position')
const beforeModelTopChangeHistoryOn = useState(
  'before_model_top_change_history_on',
  () => new Map()
)
const currentModelSwitchOnFlg = useState(
  'current_model_switch_on_flg',
  () => false
)
const currentModelSwitchHideFlg = useState(
  'current_model_switch_hide',
  () => false
)
const carModelList: Ref<CarModelContents> = useState('car_model_list')
const allCarModels: Ref<Array<CarModel[]>> = ref([])
const filteredCarModels: Ref<Array<CarModel[]>> = ref([])
const carModels: Ref<Array<CarModel[]>> = ref([])
const allSyllabary: Ref<string[]> = ref([])
const filteredSyllabary: Ref<string[]> = ref([])
const syllabary: Ref<string[]> = ref([])

useHead({
  title: TITLE_PAGE.car_catalog_list,
})

/* eslint @typescript-eslint/no-explicit-any: 0 */
const groupBySyllabary = (sortData: CarModel[]) => {
  const groupBy = (array: CarModel[], f: any) => {
    const groups: {
      [key: string]: CarModel[]
    } = {}
    array.forEach((item) => {
      const group = JSON.stringify(f(item))
      const bucket = groups[group] ?? []
      bucket.push(item)
      groups[group] = bucket
    })
    // Object.keys().map() だと要素型に undefined が混ざるため values で直接返す(順序は同一)
    return Object.values(groups)
  }
  const sorted = groupBy(sortData, (item: CarModel) => {
    return item.group_key
  })
  return sorted
}

// 条件検索からの遷移時は前画面で取得済みの一覧をそのまま使い、それ以外は API から取得する。
// 分岐ごと useAsyncData のハンドラに入れることで、取得する場合のみ payload 転送が効く
const { data: fetchedModels, error: fetchedModelsError } = await useAsyncData<
  Array<CarModel>
>(buildAsyncDataKey('car-model-list', route.fullPath), async () => {
  if (carModelList.value && query.transition_source === 'condition') {
    return carModelList.value.cars
  }
  const result = await $fetch<CarModelResponse>('/api/car-model-list/models', {
    cache: 'no-cache',
    query: query,
  })
  return result?.data?.cars instanceof Array ? result.data.cars : []
})
if (fetchedModelsError.value || (fetchedModels.value?.length ?? 0) === 0) {
  throw createError({ fatal: true })
}
modelList.value = fetchedModels.value ?? []
displayList.value = fetchedModels.value ?? []
// all
allCarModels.value = groupBySyllabary(modelList.value as Array<CarModel>)
allCarModels.value.forEach((car: CarModel[]) => {
  // groupBySyllabary 直後のため各グループは必ず1件以上ある
  allSyllabary.value.push(car[0]!.group_key)
})

// filtered
filteredModelList.value = modelList.value.filter(
  (model: CarModel) => model.current_model_flg
)
filteredCarModels.value = groupBySyllabary(
  filteredModelList.value as Array<CarModel>
)
filteredCarModels.value.forEach((car: CarModel[]) => {
  // groupBySyllabary 直後のため各グループは必ず1件以上ある
  filteredSyllabary.value.push(car[0]!.group_key)
})

currentModelSwitchHideFlg.value =
  modelList.value.length === filteredModelList.value.length

function filterNowOnSale() {
  const element = <HTMLInputElement>document.getElementById('nowOnSale')
  if (element?.checked) {
    currentModelSwitchOnFlg.value = true
    displayList.value = filteredModelList.value
    carModels.value = filteredCarModels.value
    syllabary.value = filteredSyllabary.value
  } else {
    currentModelSwitchOnFlg.value = false
    displayList.value = modelList.value
    carModels.value = allCarModels.value
    syllabary.value = allSyllabary.value
  }
}

const goToLink = (groupName: string) => {
  let el = document.getElementById('link_' + groupName)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

onMounted(() => {
  beforeModelTopChangeHistoryOn.value = new Map()

  const data = { Screen: 'CatalogCarList', ContentGroup: 'カタログ' }
  postWindowBridge('gtagApp', JSON.stringify(data))
  postWindowBridge('toggleCloseButton', false)
  postWindowBridge('toggleTopButton', false)
  postWindowBridge('toggleShareButton', false)
  postWindowBridge('toggleBottomBar', false)
  postWindowBridge('setAppBarTitle', TITLE_PAGE.car_catalog_list)
  postWindowBridge('toggleFavoriteButton', false)
  sectionHeight.value = (window.innerHeight - 64) / 11
  isLoading.value = false
  if (Number(pagePosition.value) < window.history.state.position) {
    currentModelSwitchOnFlg.value =
      query.transition_source !== TRANSITION_SOURCE.condition_find
  }
  if (currentModelSwitchOnFlg.value) {
    displayList.value = filteredModelList.value
    carModels.value = filteredCarModels.value
    syllabary.value = filteredSyllabary.value
  } else {
    displayList.value = modelList.value
    carModels.value = allCarModels.value
    syllabary.value = allSyllabary.value
  }
  pagePosition.value = window.history.state.position
})
</script>
<template>
  <SpinnerCircleSpinner :is-loading="isLoading" />
  <div v-if="!isLoading" class="all">
    <div class="left-cassette">
      <div class="cautionary-container">
        <span class="cautionary">
          ※表示される車種は当該モデルの最も新しいグレードの写真となります。
        </span>
      </div>
      <div v-if="!currentModelSwitchHideFlg" class="btn_area">
        <div class="form-switch btn_left">
          <div class="all-grade-btn">
            <div class="change-btn">現行販売車</div>
            <label class="switch">
              <input
                id="nowOnSale"
                type="checkbox"
                :checked="currentModelSwitchOnFlg"
                @click="filterNowOnSale"
              />
              <div class="slider round" />
            </label>
          </div>
        </div>
      </div>
      <div>
        <DatadisplayCarModelList
          :key="'DatadisplayCarModelList-' + String(currentModelSwitchOnFlg)"
          :car-models="carModels"
        />
      </div>
    </div>
    <div class="model_list_style">
      <div class="fixed">
        <div
          v-for="syll in syllabary"
          :key="syll"
          class="syllabary_list"
          :style="{ height: sectionHeight + 'px' }"
          @click="goToLink(syll)"
        >
          <span class="syllabary_link">{{ syll }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.all {
  display: flex;
}

.left-cassette {
  width: 87%;
}

.cautionary-container {
  padding: 12px 8px 0px 10px;
}

.cautionary {
  font-size: 11px;
}

.btn_area {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 12px 10px 10px 12px;
  font-size: 11px;
  font-weight: bold;
}
.form-switch {
  display: flex;
  align-items: center;
  text-align: center;
}
.btn_left {
  display: flex;
  padding-left: 0px;
}
.all-grade-btn {
  align-items: center;
  display: flex;
}

.change-btn {
  max-width: 200px;
  font-size: 11px;
  font-weight: bold;
  text-align: left;
  margin-right: 6px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}
.switch input {
  display: none;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9e9;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: #ffffff;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}
input:checked + .slider {
  background-color: #df2020;
}

input:focus + .slider {
  box-shadow: 0 0 1px #df2020;
}

input:checked + .slider:before {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

.slider.round {
  border-radius: 22px;
}

.slider.round:before {
  border-radius: 50%;
}

.model_list_style {
  position: fixed;
  width: 13%;
  font-size: 11px;
  background: #aaaaaa;
  display: flex;
  justify-content: center;
  text-align: center;
  min-height: calc(100vh);
  z-index: 2;
  right: 0;
}

.syllabary_list {
  display: flex;
  align-items: center;
}

.syllabary_link {
  text-decoration: none;
  line-height: 40px;
  color: #ffffff;
  font-size: 11px;
  width: 100%;
}

.syllabary_link:active {
  color: black;
  background-color: rgba(255, 255, 255, 0.4);
  opacity: 50%;
  border-radius: 10px;
}

.fixed {
  position: fixed;
  width: 13%;
  padding-top: 18px;
}
</style>
